/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useState } from 'react';
import {
	Card,
	Stack,
	Typography,
	FormLabel,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Skeleton,
	Link,
} from '@mui/material';
import { DEAL_STATUS_TYPES } from '@/utils/constants';
import { CollapsibleTaskGroup } from '@/components/CollapsibleTaskGroup';
import { DealCardStageChange } from '@/components/cards/DealCardValues/DealCardStageChange';
import { AssociatedClient } from '@/components/cards/DealCardValues/AssociatedClient';
import { useObjectContext } from '@/hooks/useObjectContext';
import { NavigateInsightButton } from '@/components/buttons/NavigateInsightButton';
import { ExpandMore } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { CompleteDealButton } from '@/components/buttons/CompleteDealButton';
import { usePipelineContext } from '@/context/pipelineContext';
import { DealMenu } from '@/components/DealMenu';
import { YearFolderPicker } from '@/components/cards/DealCardValues/YearFolderSelectorChange';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OutstandingDaysCounter } from '@/components/cards/DebtorOutstandingDaysCard';
import { useListContactsQuery } from '@/features/contacts/contactsApi';
import { ChangeAssigneeField } from '@/components/cards/DealCardValues/ChangeAssigneeField';
import { openDialog } from '@/features/dialog/dialogSlice';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';

const DebtorsAboutAccordion = ({ title, children }) => {
	const [expanded, setExpanded] = useState(true);

	return (
		<Accordion
			elevation={0}
			disableGutters
			square
			expanded={expanded}
			onChange={() => setExpanded((prev) => !prev)}
			sx={{
				backgroundColor: 'transparent',
				width: '100%',
				'&:not(:last-child)': {
					borderBottom: 0,
				},
				'&::before': {
					display: 'none',
				},
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMore sx={(theme) => ({ color: theme.palette.primary.main })} />}
				sx={(theme) => ({
					borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
					padding: 0,
					marginBottom: '1em',
				})}
			>
				<Typography variant='subtitle1'>{title}</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ padding: '0px' }}>{children}</AccordionDetails>
		</Accordion>
	);
};

export const DebtorsAboutCard = () => {
	const { dealId, dealRow, clientRow, broker } = useObjectContext();
	const dispatch = useDispatch();
	const clientId = getDealClientId(dealRow);

	const { data: contacts, ...contactsQuery } = useListContactsQuery(clientId, {
		skip: !clientId,
	});

	const mainContact = useMemo(() => contacts?.find((contact) => contact?.IsPrimary), [contacts]);

	const pipelineId = dealRow?.pipeline;
	const { stages, ...pipelineQuery } = usePipelineContext(pipelineId);

	const { data: policyData, ...policiesQuery } = useGetDealPoliciesQuery(
		{ dealId },
		{ skip: !dealId },
	);
	const policies = policyData?.policies ?? [];

	const isDeal = dealRow?.objectType === 'deal';

	const dealStage = useMemo(() => {
		const stageId = dealRow?.dealStage;
		const stage = stages?.find((stage) => stage.id === stageId);
		return stage;
	}, [stages, dealRow?.dealStage]);

	//Change this to use the debtors closed boolean value for stages
	const isDisabled = useMemo(() => dealStage?.closed || dealStage?.completed, [dealStage]);

	const isDealArchived = useMemo(
		() => dealRow?.status === DEAL_STATUS_TYPES.archived,
		[dealRow?.status],
	);

	const contactName = mainContact?.Name ?? '-';
	const contactEmail = mainContact?.Email ?? '-';
	const contactMobile = mainContact?.Mobile ?? '-';
	const isContactsLoading = contactsQuery.isLoading || contactsQuery.isUninitialized;

	return (
		<Card
			variant='about-card'
			data-testid='about-card'
			sx={{ px: 3, py: 2, borderTopRightRadius: 0, borderTopLeftRadius: 0, height: '100%' }}
		>
			<Stack direction='column' spacing={2}>
				<Stack spacing={1}>
					<OutstandingDaysCounter />
					<Typography variant='subtitle1'>Actions</Typography>
					{broker?.ledger && clientRow?.insightId && (
						<NavigateInsightButton insightId={clientRow?.insightId} />
					)}
					<DealMenu />
					{!isDisabled && !isDealArchived && (
						<>
							{!pipelineQuery.isSuccess ? (
								<Skeleton height='3em' />
							) : (
								<CompleteDealButton
									pipelineId={pipelineId}
									isDisabled={isDisabled}
								/>
							)}
						</>
					)}
				</Stack>
				<DebtorsAboutAccordion title='Compliance'>
					<Stack direction='column' spacing={3}>
						<YearFolderPicker minimal={true} />
						<DealCardStageChange />
					</Stack>
				</DebtorsAboutAccordion>
				<DebtorsAboutAccordion title='About'>
					<Stack direction='column' spacing={2}>
						<AssociatedClient />
						<ChangeAssigneeField key={'debtor-card-assignee'} />
						<Stack direction='row' justifyContent='space-between' alignItems='center'>
							<Typography variant='subtitle1'>Contact Details</Typography>
							<IconButton
								size='small'
								disabled={!clientId}
								onClick={() =>
									dispatch(
										openDialog({
											dialogName: 'editContact',
											data: { clientId, contactId: mainContact?.Id },
										}),
									)
								}
							>
								<EditIcon
									fontSize='small'
									sx={(theme) => ({ color: theme.palette.primary.main })}
								/>
							</IconButton>
						</Stack>
						<Stack>
							<FormLabel>{'Primary Contact'}</FormLabel>
							{isContactsLoading ? (
								<Skeleton width='100%' />
							) : (
								<Typography variant='subtitle2' sx={{ color: 'grey' }}>
									{contactName}
								</Typography>
							)}
						</Stack>
						<Stack>
							<FormLabel>{'Primary Contact email'}</FormLabel>
							{isContactsLoading ? (
								<Skeleton width='100%' />
							) : (
								<Typography variant='subtitle2' sx={{ color: 'grey' }}>
									{mainContact?.Email ? (
										<Link
											href={`mailto:${contactEmail}`}
											underline='hover'
											sx={(theme) => ({
												color: 'inherit',
												'&:hover': { color: theme.palette.primary.main },
											})}
										>
											{contactEmail}
										</Link>
									) : (
										contactEmail
									)}
								</Typography>
							)}
						</Stack>
						<Stack>
							<FormLabel>{'Mobile Phone'}</FormLabel>
							{isContactsLoading ? (
								<Skeleton width='100%' />
							) : (
								<Typography variant='subtitle2' sx={{ color: 'grey' }}>
									{mainContact?.Mobile ? (
										<Link
											href={`tel:${contactMobile.replace(/\s/g, '')}`}
											underline='hover'
											sx={(theme) => ({
												color: 'inherit',
												'&:hover': { color: theme.palette.primary.main },
											})}
										>
											{contactMobile}
										</Link>
									) : (
										contactMobile
									)}
								</Typography>
							)}
						</Stack>
					</Stack>
				</DebtorsAboutAccordion>
				<CollapsibleTaskGroup />
			</Stack>
		</Card>
	);
};
